"use client";

import { useState } from "react";
import RangeSlider from "./RangeSlider";

interface PercentInputProps {
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  id?: string;
  showSlider?: boolean;
}

export default function PercentInput({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 0.01,
  label,
  id,
  showSlider = true,
}: PercentInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    if (inputVal === "") {
      onChange(0);
      return;
    }

    let num = parseFloat(inputVal);
    if (isNaN(num)) return;
    
    if (num > max) num = max;
    if (num < min) num = min;
    
    onChange(num);
  };

  return (
    <div className="percent-input-wrapper">
      {label && <label htmlFor={id} className="input-label">{label}</label>}
      <div className={`percent-input-container ${isFocused ? 'focused' : ''}`}>
        <input
          type="number"
          id={id}
          className="percent-input-field"
          value={value || ""}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          step={step}
          min={min}
          max={max}
          placeholder="0.00"
        />
        <div className="percent-suffix-box">
          <span className="percent-symbol">%</span>
        </div>
      </div>

      {showSlider && (
        <div className="slider-wrapper">
          <RangeSlider
            value={value}
            onChange={onChange}
            min={min}
            max={max}
            step={step}
            suffix="%"
          />
        </div>
      )}

      <style jsx>{`
        .percent-input-wrapper {
          width: 100%;
          margin-bottom: 0;
        }
        .percent-input-container {
          display: flex;
          align-items: stretch;
          background-color: var(--bg-input);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          overflow: hidden;
          transition: all var(--transition-fast);
          height: 3rem;
        }

        .percent-input-container.focused {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px var(--primary-light);
        }
        .percent-input-field {
          flex: 1;
          border: none;
          background: transparent;
          color: var(--text-primary);
          font-family: var(--font-body);
          font-size: var(--text-base);
          font-weight: var(--fw-bold);
          padding: 0 1rem;
          outline: none;
          text-align: center;
          min-width: 0;
        }
        .percent-input-field::placeholder {
          color: var(--text-muted);
          opacity: 0.5;
        }
        .percent-input-field::-webkit-outer-spin-button,
        .percent-input-field::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .percent-input-field {
          -moz-appearance: textfield;
        }
        .percent-suffix-box {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 0.875rem;
          background-color: var(--bg-surface-hover);
          border-left: 1px solid var(--border-color);
          min-width: 3rem;
        }
        .percent-symbol {
          color: var(--text-muted);
          font-weight: var(--fw-extrabold);
          font-size: var(--text-sm);
        }
        .slider-wrapper {
          margin-top: var(--space-3);
          padding: 0 var(--space-2);
        }
      `}</style>
    </div>
  );
}
